import { useCallback } from 'react';

import { Permission } from '../../../backend/src/auth/permissions/permission';
import { useMatterPermissions } from '../Contexts/MatterPermission';
import { useUserContext } from '../Contexts/User';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

/** Derive a human-readable message from a permission string like "subset:create" → "You do not have permission to create a subset" */
function defaultDisabledMessage(permission: string): string {
  const [resource, action] = permission.split(':');
  const resourceWords = resource
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
  const actionWords = action.replace(/-/g, ' ');
  const article = /^[aeiou]/i.test(resourceWords) ? 'an' : 'a';
  return `You do not have permission to ${actionWords} ${article} ${resourceWords}`;
}

/**
 * Returns the permission set to check against: effective (matter-level override)
 * permissions when inside a matter context, otherwise the user's global role permissions.
 */
function useEffectivePermissions(): Permission[] {
  const { user } = useUserContext();
  const { effectivePermissions } = useMatterPermissions();
  return effectivePermissions ?? user?.role?.permissions ?? [];
}

export function useHasPermission() {
  const permissions = useEffectivePermissions();

  const hasPermission = useCallback(
    (permission: Permission) => {
      return permissions.includes(permission);
    },
    [permissions],
  );

  return { hasPermission };
}

export function usePermission(permission: Permission) {
  const permissions = useEffectivePermissions();
  const allowed = permissions.includes(permission);
  return { allowed };
}

interface PermissionGateProps {
  permission: Permission | Permission[];
  orAllowed?: boolean;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  uiMode?: 'hidden' | 'disabled';
  disabledMessage?: string;
  requestSupport?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
}

export function PermissionGate({
  permission,
  orAllowed,
  children,
  fallback = null,
  uiMode = 'hidden',
  disabledMessage,
  requestSupport = false,
  side,
  sideOffset,
}: PermissionGateProps) {
  const perms = Array.isArray(permission) ? permission : [permission];
  const effectivePermissions = useEffectivePermissions();
  const allowed = perms.some((p) => effectivePermissions.includes(p));

  if (allowed || orAllowed) return <>{children}</>;
  if (uiMode === 'hidden') return <>{fallback}</>;

  // disabled mode
  const message =
    disabledMessage ??
    (perms.length > 1 ? 'You do not have permission to use this feature' : defaultDisabledMessage(perms[0]));

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-not-allowed opacity-50">
          <div className="pointer-events-none">{children}</div>
        </div>
      </TooltipTrigger>
      <TooltipContent side={side} sideOffset={sideOffset}>
        <p>{message}</p>
        {requestSupport && (
          <p className="mt-1 text-xs opacity-75">
            Contact{' '}
            <a href="mailto:support@wexler.ai" className="underline">
              support@wexler.ai
            </a>{' '}
            to request access.
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
